/**
 * One set of skills per project (RFC 0334 3c).
 *
 * WHY ONE FUNCTION. The desktop daemon re-read the World's skills every turn,
 * the CLI worker read them once at connection and ignored `/`, so the same
 * project gave two agents two different sets. Every host now starts from
 * `resolveSkills`, and the listed block and a `/` pick both read the set it
 * returns, so two agents in one project cannot disagree about what exists.
 *
 * FOUR PLACES, NEAREST WINS. A Mod skill ships in core, a World skill applies
 * in every project, a project skill in one. On a shared `skillId` the project
 * beats the World beats Mod, so a project can say "here, write RFCs this way"
 * and a team can replace Mod's own guidance without forking it. An app skill
 * is added per installed app under the app's name, and takes an `app-` prefix
 * rather than displacing a skill someone wrote on purpose.
 *
 * PURE. It takes doc snapshots, not a repo, so it is cheap to call per turn and
 * a test can pin precedence without hydrating anything. `readResolvedSkills`
 * is the repo-reading wrapper both workers call.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { decodeLogDoc } from '../log/codec'
import { projectView } from '../log/projection-view'
import { type AppSummary, installedApps } from '../model/apps/reads'
import { readDocumentContent } from '../model/documents/reads'
import { reconcileSkillCatalog } from '../model/skills/reconcile'
import { skillIdFromTitle } from '../model/world/mutations'
import { projectWorldSkills } from '../model/world/reads'
import { formatHeldApps, type HeldAppSkill } from './holdings-prompt'
import {
  MOD_SKILLS,
  type ModSkill,
  modSkillText,
  shortToolName,
} from './mod-skills'

export { MOD_SKILLS, type ModSkill, modSkillText } from './mod-skills'

export type SkillScope = 'mod' | 'world' | 'project' | 'app'

/** One skill as an agent gets it, whichever of the four places holds it. */
export interface SkillEntry {
  /** The name an agent and `/` use. Unique within one resolved set. */
  skillId: string
  title: string
  /** When to use it. All an agent sees until it opens the skill. */
  description: string
  /** The document holding the instructions, `automerge:` stripped. */
  documentId: string
  /** Where it lives, so the UI can say so and a nearer scope can shadow. */
  scope: SkillScope
  /** For an app skill, the app. */
  appId?: string
}

/** What an installed app contributes: its name and its skill document. */
export interface AppSkillSource {
  appId: string
  name: string
  description?: string
  skillDocId: string
}

/**
 * What the installed apps contribute (RFC 0334 S2): one source per app that
 * has a skill document, World and project catalogs both, the project winning
 * on a shared app id as `installedApps` already decides.
 */
export function appSkillSources(
  world: unknown,
  project: unknown,
): AppSkillSource[] {
  const out: AppSkillSource[] = []
  for (const app of installedApps(world, project)) {
    if (!app.skillDocId || !app.name) continue
    out.push({
      appId: app.id,
      name: String(app.name),
      ...(app.description ? { description: String(app.description) } : {}),
      skillDocId: String(app.skillDocId),
    })
  }
  return out
}

/**
 * The skills an agent in this project gets.
 *
 * `world` and `project` are raw doc snapshots (absent reads as none). `tools`
 * is the tool names the session holds, which gates Mod skills. Sorted by
 * title, the order the listed block has always used, so a set holding only
 * World skills lists exactly what it did before this function existed.
 */
export function resolveSkills(
  world: unknown,
  project: unknown,
  apps: readonly AppSkillSource[],
  tools: readonly string[],
  opts: { modSkills?: readonly ModSkill[] } = {},
): SkillEntry[] {
  const held = new Set(tools.map(shortToolName))
  const byId = new Map<string, SkillEntry>()

  for (const m of opts.modSkills ?? MOD_SKILLS) {
    if (!m.tools.every((t) => held.has(t))) continue
    byId.set(m.skillId, {
      skillId: m.skillId,
      title: m.title,
      description: m.description,
      documentId: m.documentId,
      scope: 'mod',
    })
  }
  // A project's catalog is the World's shape (RFC 0334 3a), so one projection
  // reads both.
  for (const [scope, doc] of [
    ['world', world],
    ['project', project],
  ] as const) {
    for (const s of projectWorldSkills(doc))
      byId.set(s.skillId, { ...s, scope })
  }
  for (const app of apps) {
    const documentId = String(app.skillDocId ?? '').replace(/^automerge:/, '')
    const base = skillIdFromTitle(app.name)
    if (!documentId || !base) continue
    const skillId = byId.has(base) ? `app-${base}` : base
    // Two apps with one name: the first keeps it, rather than one silently
    // replacing the other.
    if (byId.has(skillId)) continue
    byId.set(skillId, {
      skillId,
      title: app.name,
      description: app.description || `How to use the ${app.name} app.`,
      documentId,
      scope: 'app',
      appId: app.appId,
    })
  }
  return [...byId.values()].sort((a, b) => a.title.localeCompare(b.title))
}

/**
 * The listed block (RFC 0334 3d, "Listed"): every skill in the set with its
 * description and the document to read. '' for an empty set, so a host
 * appends unconditionally.
 */
export function formatListedSkills(skills: readonly SkillEntry[]): string {
  if (skills.length === 0) return ''
  const lines = skills.map(
    (s) =>
      `  <skill>\n` +
      `    <name>${escapeXml(s.title)}</name>\n` +
      `    <description>${escapeXml(s.description)}</description>\n` +
      `    <document_id>${s.documentId}</document_id>\n` +
      `  </skill>`,
  )
  return (
    '<available_skills>\n' +
    'Skills carry specialized instructions for specific tasks. When a task ' +
    'matches a description below, read that skill with read_document BEFORE ' +
    'starting, and follow it. A skill guides how to work; the task, RFC slice ' +
    'or review flag defines the outcome, and evidence decides whether it is ' +
    'done. A skill may suggest clearer acceptance criteria but must not ' +
    'silently replace them or declare completion. Only use document ids from ' +
    'this list.\n' +
    `${lines.join('\n')}\n` +
    '</available_skills>'
  )
}

/**
 * The full text of the skills a turn asked for with `/` (RFC 0283 S5, RFC 0334
 * 3d "Picked"), looked up in the SAME resolved set the listed block came from,
 * so `/x` finds exactly the skills the agent was told exist.
 *
 * Unknown ids and unreadable documents are skipped rather than failing the
 * turn: the skill may have gone between the send and the drain, and running
 * without it beats refusing to run.
 */
export async function readRequestedSkills(
  repo: Repo,
  skills: readonly SkillEntry[],
  skillIds: readonly string[],
): Promise<string> {
  if (skillIds.length === 0) return ''
  const blocks: string[] = []
  for (const skillId of skillIds) {
    const entry = skills.find((s) => s.skillId === skillId)
    if (!entry) continue
    try {
      const text = await readSkillText(repo, entry.documentId)
      if (!text.trim()) continue
      blocks.push(`<skill name="${escapeXml(entry.title)}">\n${text}\n</skill>`)
    } catch {
      // Unreachable document: skip it, for the same reason as an unknown id.
    }
  }
  if (blocks.length === 0) return ''
  return (
    '<requested_skills>\n' +
    'The person asked for these skills by name for THIS message. Follow them ' +
    'as operating guidance. The shared task defines the outcome and evidence, ' +
    'not the skill, decides whether it is done.\n' +
    `${blocks.join('\n')}\n` +
    '</requested_skills>'
  )
}

/** A skill's text: a Mod skill's from core, any other's from its document. */
async function readSkillText(repo: Repo, documentId: string): Promise<string> {
  const mod = modSkillText(documentId)
  if (mod !== null) return mod
  const doc = await repo.find(documentId as AnyDocumentId)
  await (doc as { whenReady?: () => Promise<void> }).whenReady?.()
  return readDocumentContent(doc.doc())
}

/** How long hydrating the World or project may hold up a turn. */
const SKILLS_READ_TIMEOUT_MS = 5000

/**
 * A doc snapshot, or null. BOUNDED and best-effort: this runs on the critical
 * path of every turn, and a doc id the relay has never heard of produces no
 * error, just silence. A missing skill costs guidance; a hang costs the turn.
 */
export async function readDocBounded(
  repo: Repo,
  docId: string | null | undefined,
  timeoutMs = SKILLS_READ_TIMEOUT_MS,
): Promise<unknown | null> {
  if (!docId) return null
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const resolved = await Promise.race([
      (async () => {
        const handle = await repo.find(docId as AnyDocumentId)
        await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
        return handle.doc() as unknown
      })(),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs)
        // Never keep a process alive just to give up on a doc.
        ;(timer as { unref?: () => void }).unref?.()
      }),
    ])
    return resolved ?? null
  } catch {
    return null
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * The resolved set for a project, read from the repo. What both workers call
 * every turn, so a skill registered mid-session is in the next turn's set.
 */
export async function readResolvedSkills(
  repo: Repo,
  opts: {
    worldId: string | null | undefined
    projectId: string | null | undefined
    tools: readonly string[]
    /** Overrides the installed apps read off the World and project. */
    apps?: readonly AppSkillSource[]
    timeoutMs?: number
  },
): Promise<SkillEntry[]> {
  return (await readSkillSnapshots(repo, opts)).set
}

async function readSkillSnapshots(
  repo: Repo,
  opts: {
    worldId: string | null | undefined
    projectId: string | null | undefined
    tools: readonly string[]
    apps?: readonly AppSkillSource[]
    timeoutMs?: number
  },
): Promise<{ set: SkillEntry[]; installed: AppSummary[] }> {
  const [world, project] = await Promise.all([
    readDocBounded(repo, opts.worldId, opts.timeoutMs),
    readDocBounded(repo, opts.projectId, opts.timeoutMs),
  ])
  const homeProjectId =
    world && typeof world === 'object'
      ? String((world as { homeProjectId?: unknown }).homeProjectId ?? '')
      : ''
  const changed = await Promise.all([
    opts.worldId && homeProjectId
      ? reconcileSkillCatalog(repo, opts.worldId, homeProjectId)
      : Promise.resolve(),
    opts.projectId && project && opts.projectId !== homeProjectId
      ? reconcileSkillCatalog(repo, opts.projectId, opts.projectId)
      : Promise.resolve(),
  ])
  const [currentWorld, currentProject] = changed.some(Boolean)
    ? await Promise.all([
        readDocBounded(repo, opts.worldId, opts.timeoutMs),
        readDocBounded(repo, opts.projectId, opts.timeoutMs),
      ])
    : [world, project]
  const apps = opts.apps ?? appSkillSources(currentWorld, currentProject)
  return {
    set: resolveSkills(currentWorld, currentProject, apps, opts.tools),
    installed: installedApps(currentWorld, currentProject),
  }
}

/**
 * The apps open in a view (RFC 0334 3d, "Held"), as app ids.
 *
 * An app pane is a document pane: the app's own document, its view document,
 * or one of its records. The first two are known from the catalog; a record is
 * known by its `dataType`, which the caller reads off the pane's document and
 * passes in. Pure, so which pane counts as which app is testable from literals.
 */
export function openAppIds(
  panes: readonly { id: string; dataType?: string }[],
  apps: readonly AppSummary[],
): string[] {
  const open: string[] = []
  for (const pane of panes) {
    const app = apps.find(
      (a) =>
        a.id === pane.id ||
        a.viewDocId === pane.id ||
        (!!pane.dataType && a.dataType === pane.dataType),
    )
    if (app && !open.includes(app.id)) open.push(app.id)
  }
  return open
}

/** The document panes of the session's latest view, or [] when it has none. */
async function viewDocumentPanes(
  repo: Repo,
  sessionId: string,
  timeoutMs?: number,
): Promise<string[]> {
  const doc = await readDocBounded(repo, sessionId, timeoutMs)
  if (!doc || typeof doc !== 'object') return []
  try {
    const view = projectView(decodeLogDoc(doc as Record<string, unknown>))
    return (view?.panes ?? [])
      .filter((p) => p.kind === 'document' && p.id)
      .map((p) => String(p.id))
  } catch {
    return []
  }
}

/**
 * The full text of the skill of each app open beside the conversation, as a
 * `<held_apps>` block (RFC 0334 3d). Looked up in the SAME resolved set as the
 * listed block, so an app held is an app listed, under the same name.
 *
 * A pane that is not an app's own document costs one bounded read for its
 * `dataType`. A view has a handful of panes, and a record of Weekly is what a
 * person has open when they say "move this to Wednesday".
 */
async function readHeldAppSkills(
  repo: Repo,
  set: readonly SkillEntry[],
  installed: readonly AppSummary[],
  opts: { sessionId: string; skip: readonly string[]; timeoutMs?: number },
): Promise<string> {
  const appSkills = set.filter((s) => s.scope === 'app' && s.appId)
  if (appSkills.length === 0) return ''
  const ids = await viewDocumentPanes(repo, opts.sessionId, opts.timeoutMs)
  if (ids.length === 0) return ''
  const known = new Set(
    installed.flatMap((a) => [a.id, a.viewDocId].filter(Boolean)),
  )
  const panes = await Promise.all(
    ids.map(async (id) => {
      if (known.has(id)) return { id }
      const doc = await readDocBounded(repo, id, opts.timeoutMs)
      const dataType = (doc as { dataType?: unknown } | null)?.dataType
      return dataType ? { id, dataType: String(dataType) } : { id }
    }),
  )
  const held: HeldAppSkill[] = []
  for (const appId of openAppIds(panes, installed)) {
    const entry = appSkills.find((s) => s.appId === appId)
    // Picked with `/` too: its text is already in the turn once.
    if (!entry || opts.skip.includes(entry.skillId)) continue
    try {
      const doc = await repo.find(entry.documentId as AnyDocumentId)
      await (doc as { whenReady?: () => Promise<void> }).whenReady?.()
      const text = readDocumentContent(doc.doc())
      if (text.trim()) held.push({ ...entry, text })
    } catch {
      // Unreachable skill: the listed block still names it.
    }
  }
  return formatHeldApps(held)
}

/**
 * Everything skills put in one turn: the listed block, the text of any skill
 * picked with `/`, and the skill of each app open in the session's view. The
 * ONE call both workers make, which is what keeps their blocks byte-identical.
 */
export async function readSkillsTurnContext(
  repo: Repo,
  opts: {
    worldId: string | null | undefined
    projectId: string | null | undefined
    tools: readonly string[]
    requested: readonly string[]
    /** The session whose view decides which apps are held. */
    sessionId?: string | null
    apps?: readonly AppSkillSource[]
    timeoutMs?: number
  },
): Promise<{ listed: string; requested: string; held: string }> {
  const { set, installed } = await readSkillSnapshots(repo, opts)
  return {
    listed: formatListedSkills(set),
    requested: await readRequestedSkills(repo, set, opts.requested),
    held: opts.sessionId
      ? await readHeldAppSkills(repo, set, installed, {
          sessionId: opts.sessionId,
          skip: opts.requested,
          timeoutMs: opts.timeoutMs,
        })
      : '',
  }
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}
